import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { FaSearch } from 'react-icons/fa';
import { API_BASE, WEBSITE_URL, parseJsonResponse, getUserErrorMessage } from '../../utils/apiBase';
import { authFetch } from '../../utils/apiAuth';
import { isSuperAdmin } from '../../utils/authSession';
import { getAffiliatePageStyles } from './affiliateStyles';
import './affiliates.css';
import ProductLinkModal from './ProductLinkModal';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import { buildAffiliateLink, copyTextToClipboard } from '../../utils/affiliateLinks';

const statusClass = {
  active: 'statusActive',
  inactive: 'statusInactive',
  suspended: 'statusSuspended',
};

export default function AffiliatesList() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [linkModalCode, setLinkModalCode] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [confirmDelete, setConfirmDelete] = useState({ open: false, affiliate: null });
  const showDeleteAction = isSuperAdmin();

  const styles = getAffiliatePageStyles(isMobile, isSmallMobile);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchAffiliates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: '1', limit: '100' });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (statusFilter) params.set('status', statusFilter);

      const response = await fetch(`${API_BASE}/v1/affiliate?${params.toString()}`);
      const result = await parseJsonResponse(response);
      setAffiliates(result.data || []);
    } catch (error) {
      setAffiliates([]);
      toast.error(getUserErrorMessage(error, 'Unable to load affiliates. Please try again later.'));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchAffiliates();
  }, [fetchAffiliates]);

  const copyStoreLink = async (code) => {
    try {
      await copyTextToClipboard(buildAffiliateLink(code, WEBSITE_URL));
      toast.success('Store affiliate link copied');
    } catch {
      toast.error('Unable to copy link');
    }
  };

  const openDeleteModal = (affiliate) => {
    setConfirmDelete({ open: true, affiliate });
  };

  const closeDeleteModal = () => {
    if (deletingId) return;
    setConfirmDelete({ open: false, affiliate: null });
  };

  const confirmDeleteAffiliate = async () => {
    const item = confirmDelete.affiliate;
    if (!item) return;

    if (item.totalOrders > 0) {
      toast.error('Affiliates with orders cannot be deleted. Set status to Inactive instead.');
      closeDeleteModal();
      return;
    }

    setDeletingId(item._id);
    try {
      const response = await authFetch(`${API_BASE}/v1/affiliate/${item._id}`, {
        method: 'DELETE',
      });
      await parseJsonResponse(response);
      toast.success('Affiliate deleted');
      setConfirmDelete({ open: false, affiliate: null });
      fetchAffiliates();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to delete affiliate.'));
    } finally {
      setDeletingId('');
    }
  };

  return (
    <div style={styles.mainWrapper}>
      <Toaster position="top-center" />
      {linkModalCode && (
        <ProductLinkModal
          affiliateCode={linkModalCode}
          onClose={() => setLinkModalCode('')}
        />
      )}
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div>
            <h1 style={styles.headerTitle}>Affiliates</h1>
            <p style={styles.breadcrumbs}>Home • Affiliate List</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/affiliates/add')}
            style={styles.primaryButton}
          >
            + Add Affiliate
          </button>
        </div>

        <div style={styles.contentContainer}>
          <div style={styles.toolbar}>
            <div className={`affiliate-search-bar${isMobile ? ' affiliate-search-bar--full' : ''}`}>
              <FaSearch className="affiliate-search-icon" />
              <input
                type="search"
                name="affiliateSearch"
                className="affiliate-search-input"
                placeholder="Search by name or code"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                data-lpignore="true"
                data-form-type="other"
              />
            </div>
            <select
              className={`affiliate-filter-select${isMobile ? ' affiliate-search-bar--full' : ''}`}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Name', 'Code', 'Commission', 'Discount', 'Clicks', 'Orders', 'Sales', 'Status', 'Actions'].map((h) => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} style={styles.emptyCell}>Loading affiliates...</td>
                  </tr>
                ) : affiliates.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={styles.emptyCell}>No affiliates found.</td>
                  </tr>
                ) : (
                  affiliates.map((item) => (
                    <tr key={item._id}>
                      <td style={styles.td}>{item.name}</td>
                      <td style={{ ...styles.td, fontWeight: 700, color: '#D4AF37' }}>{item.code}</td>
                      <td style={styles.td}>{item.commissionRate}%</td>
                      <td style={styles.td}>
                        {item.discountType === 'none'
                          ? '—'
                          : item.discountType === 'percentage'
                            ? `${item.discountValue}%`
                            : `Rs ${item.discountValue}`}
                      </td>
                      <td style={styles.td}>{item.totalClicks || 0}</td>
                      <td style={styles.td}>{item.totalOrders || 0}</td>
                      <td style={styles.td}>Rs {(item.totalSales || 0).toLocaleString()}</td>
                      <td style={{ ...styles.td, ...styles[statusClass[item.status] || 'statusInactive'] }}>
                        {item.status}
                      </td>
                      <td style={styles.td}>
                        <div style={styles.actionGroup}>
                          <button
                            type="button"
                            onClick={() => navigate(`/affiliates/details/${item._id}`)}
                            style={styles.secondaryButton}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => copyStoreLink(item.code)}
                            style={styles.secondaryButton}
                          >
                            Copy Store Link
                          </button>
                          <button
                            type="button"
                            onClick={() => setLinkModalCode(item.code)}
                            style={styles.secondaryButton}
                          >
                            Product Link
                          </button>
                          {showDeleteAction && (
                            <button
                              type="button"
                              onClick={() => openDeleteModal(item)}
                              disabled={deletingId === item._id}
                              style={{
                                ...styles.dangerButton,
                                opacity: deletingId === item._id ? 0.6 : 1,
                                cursor: deletingId === item._id ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {deletingId === item._id ? 'Deleting...' : 'Delete'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ConfirmDeleteModal
        open={confirmDelete.open}
        message={
          confirmDelete.affiliate ? (
            <>
              Are you sure you want to delete affiliate{' '}
              <strong>&quot;{confirmDelete.affiliate.name}&quot;</strong> (
              {confirmDelete.affiliate.code})? This action cannot be undone.
            </>
          ) : (
            ''
          )
        }
        warning={
          confirmDelete.affiliate?.totalOrders > 0
            ? `This affiliate has ${confirmDelete.affiliate.totalOrders} order(s) and cannot be deleted. Set status to Inactive instead.`
            : undefined
        }
        canConfirm={!confirmDelete.affiliate?.totalOrders}
        cancelLabel={confirmDelete.affiliate?.totalOrders > 0 ? 'Close' : 'Cancel'}
        loading={Boolean(deletingId)}
        onConfirm={confirmDeleteAffiliate}
        onCancel={closeDeleteModal}
      />
    </div>
  );
}
